(() => {
  const controllers = new WeakMap();

  const initializeGallery = (gallery) => {
    if (controllers.has(gallery)) return;
    const stage = gallery.querySelector('.product-gallery');
    const controls = gallery.querySelector('[data-signature-gallery-controls]');
    const status = gallery.querySelector('[data-signature-gallery-status]');
    const buttons = [...gallery.querySelectorAll('[data-signature-thumbnail]')];
    const items = [...stage?.querySelectorAll('[data-product-media-id]') || []];
    if (!stage || items.length === 0) return;
    const byId = new Map(items.map((item) => [item.dataset.productMediaId, item]));
    const events = new AbortController();
    let activeId = null;
    let frame = 0;
    let initialized = false;

    const pauseMedia = (item) => {
      item.querySelectorAll('video').forEach((video) => video.pause());
      item.querySelectorAll('model-viewer').forEach((model) => {
        if (typeof model.pause === 'function') model.pause();
      });
      item.querySelectorAll('iframe').forEach((iframe) => {
        const source = iframe.getAttribute('src');
        if (source) iframe.setAttribute('src', source);
      });
    };

    const revealButton = (button) => {
      const strip = button?.parentElement;
      if (!button || !strip) return;
      const bounds = button.getBoundingClientRect();
      const viewport = strip.getBoundingClientRect();
      if (bounds.left < viewport.left) strip.scrollLeft -= viewport.left - bounds.left + 3;
      else if (bounds.right > viewport.right) strip.scrollLeft += bounds.right - viewport.right + 3;
    };

    const sync = () => {
      frame = 0;
      const current = items.find((item) => item.getAttribute('aria-current') === 'true') || items[0];
      const nextId = current.dataset.productMediaId;
      const changed = activeId !== nextId;
      const previous = byId.get(activeId);
      if (changed && previous) pauseMedia(previous);
      const focusWillHide = items.some((item) => item !== current && item.contains(document.activeElement));
      items.forEach((item) => { item.hidden = item !== current; });
      buttons.forEach((button) => {
        const selected = button.dataset.signatureThumbnail === nextId;
        button.setAttribute('aria-pressed', String(selected));
        button.tabIndex = selected ? 0 : -1;
      });
      const selectedButton = buttons.find((button) => button.dataset.signatureThumbnail === nextId);
      if (changed && initialized) {
        if (status) status.textContent = current.dataset.signatureAnnouncement || '';
        revealButton(selectedButton);
      }
      if (focusWillHide && selectedButton) selectedButton.focus({ preventScroll: true });
      activeId = nextId;
      initialized = true;
      gallery.dataset.signatureGalleryReady = 'true';
      if (controls) controls.hidden = false;
    };

    const select = (mediaId) => {
      if (!byId.has(mediaId)) return;
      items.forEach((item) => {
        if (item.dataset.productMediaId === mediaId) item.setAttribute('aria-current', 'true');
        else item.removeAttribute('aria-current');
      });
      sync();
    };

    buttons.forEach((button, index) => {
      button.addEventListener('click', () => select(button.dataset.signatureThumbnail), { signal: events.signal });
      button.addEventListener('keydown', (event) => {
        const rtl = getComputedStyle(button).direction === 'rtl';
        let nextIndex;
        if (event.key === 'ArrowRight') nextIndex = index + (rtl ? -1 : 1);
        else if (event.key === 'ArrowLeft') nextIndex = index + (rtl ? 1 : -1);
        else if (event.key === 'Home') nextIndex = 0;
        else if (event.key === 'End') nextIndex = buttons.length - 1;
        else return;
        event.preventDefault();
        const next = buttons[(nextIndex + buttons.length) % buttons.length];
        select(next.dataset.signatureThumbnail);
        next.focus({ preventScroll: true });
        revealButton(next);
      }, { signal: events.signal });
    });

    const observer = new MutationObserver(() => {
      if (!frame) frame = requestAnimationFrame(sync);
    });
    observer.observe(stage, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-current'] });
    sync();
    controllers.set(gallery, () => {
      observer.disconnect();
      events.abort();
      if (frame) cancelAnimationFrame(frame);
      items.forEach((item) => { item.hidden = false; });
      if (controls) controls.hidden = true;
      delete gallery.dataset.signatureGalleryReady;
      controllers.delete(gallery);
    });
  };

  const galleriesIn = (root) => {
    const galleries = [...root.querySelectorAll('[data-signature-gallery]')];
    if (root instanceof Element && root.matches('[data-signature-gallery]')) galleries.unshift(root);
    return galleries;
  };
  const initialize = (root = document) => galleriesIn(root).forEach(initializeGallery);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    galleriesIn(event.target).forEach((gallery) => controllers.get(gallery)?.());
  });
})();
